import type { ComponentProps } from "react";
import Link from "next/link";
import Image from "next/image";

import { Subline } from "@/components/typography/Subline";
import { Headline } from "@/components/typography/Headline";
import { Tag } from "@/components/typography/Tag";
// import { Lock } from "@/components/icons";
import { cn } from "@/lib/utils";

type ContentCardProps = {
  tagline?: string;
  headline: string;
  text?: string;
  href: string;
  image?: string;
  tags?: (number | string)[];
  color?: "sun" | "sky";
  reverse?: boolean;
  mask?: boolean;
} & ComponentProps<"article">;

const defaultImage = "/images/shape.svg";

const ContentCard = ({
  tagline,
  headline,
  text,
  href,
  image = defaultImage,
  tags = [],
  color = "sun",
  reverse = false,
  mask = false,
  ...rest
}: ContentCardProps) => (
  <article
    className={cn(
      "rounded-3xl card-rotate relative flex flex-col md:flex-row cursor-pointer",
      {
        "bg-sun-100": color === "sun",
        "bg-sky-75": color === "sky",
        "md:flex-row-reverse": reverse,
      },
      rest.className
    )}
  >
    <div className="w-full md:w-3/5 p-6 flex flex-col">
      <Subline>{tagline}</Subline>
      <Headline as="h3" size="tall">
        {headline}
      </Headline>

      <p className="text-copy my-4 line-clamp-5">{text}</p>

      <div className="flex items-center gap-2 flex-wrap mt-auto relative z-20">
        {tags?.map((name: number | string, index: number) => (
          <Tag
            key={index}
            label={name.toLocaleString()}
            href={`/search?tag=${name}`}
            asButton
          />
        ))}
      </div>
    </div>

    <div className="w-full md:w-2/5 order-first md:order-none aspect-[16/9] md:aspect-auto md:min-h-[300px] flex items-center justify-center">
      <Image
        src={image}
        alt=""
        width={420}
        height={420}
        className={cn(
          "w-full h-full object-cover object-center rounded-r-2xl",
          {
            "object-contain": image === defaultImage,
            "object-left rounded-l-2xl": reverse,
          }
        )}
        style={{ clipPath: mask ? "url(#mask1)" : undefined }}
      />
    </div>

    <Link
      href={href}
      className="absolute inset-0 z-10"
      aria-label={headline || "Weitere Informationen"}
    />
  </article>
);

export { ContentCard };
